import { clsx } from 'clsx'
import { useState } from 'react'

interface InputProps {
  /**
   * Field id
   */
  id?: string
  /**
   * Field name
   */
  name?: string
  /**
   * Type of field
   */
  type?: 'text' | 'email' | 'number' | 'tel' | 'password' | 'date'
  /**
   * Form label
   */
  label?: string
  /**
   * Max character length
   */
  maxLength?: number
  /**
   * Min character length
   */
  minLength?: number
  /**
   * Max value for number/date types
   */
  max?: number | string
  /**
   * Min value for number/date types
   */
  min?: number | string
  /**
   * Step increment for number fields (default 1, for decimal pass .01)
   */
  step?: number | string
  /**
   * Is disabled
   */
  disabled?: boolean
  /**
   * Is read only?
   */
  readOnly?: boolean
  /**
   * Is required
   */
  required?: boolean
  /**
   * Prepend icon
   */
  prependIcon?: boolean
  /**
   * Placeholder text
   */
  placeholder?: string
  /**
   * Value
   */
  value?: string
  /**
   * Error text
   */
  error?: string
  /**
   * Allow autocomplete
   */
  autocomplete?: string
  /**
   * Aria label
   */
  ariaLabel?: string
  /**
   * Form name
   */
  form?: string
  /**
   * Input mode
   */
  inputMode?: 'text' | 'numeric' | 'tel' | 'search' | 'email' | 'url'
  /**
   * Input pattern
   */
  pattern?: string
  /**
   * Optional blur handler
   */
  onChange?: (e: string) => void
  /**
   * Blur handler
   */
  onBlur?: () => void
  /**
   * Keydown handler
   */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
}

/**
 * Primary UI component for user interaction
 */
const Input = ({
  id,
  name,
  type = 'text',
  label,
  maxLength,
  minLength,
  max,
  min,
  step,
  disabled = false,
  readOnly = false,
  required = false,
  placeholder,
  value,
  error,
  autocomplete,
  ariaLabel,
  form,
  inputMode = 'text',
  pattern,
  onChange,
  onBlur,
  onKeyDown,
}: InputProps) => {
  const [inputType, setInputType] = useState(type)
  const kebabCase = (string: string) =>
    string
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .replace(/[\s_]+/g, '-')
      .toLowerCase()

  return (
    <div
      className={clsx('relative', {
        'flex flex-col gap-2': label,
      })}
    >
      <label className="font-medium">
        {label}
        {required && <span className="text-red-600">*</span>}
      </label>
      <input
        type={inputType}
        className={clsx(
          'block w-full border bg-beige-200 p-3 text-teal-800 placeholder:text-teal-500 focus:outline-none',
          {
            'border-red-600': error,
            'border-slate-400': !error,
          }
        )}
        name={name}
        id={id ?? (name && kebabCase(name))}
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        minLength={minLength}
        min={min}
        max={max}
        step={step}
        autoComplete={autocomplete}
        aria-label={ariaLabel}
        disabled={disabled}
        form={form}
        readOnly={readOnly}
        required={required}
        inputMode={inputMode}
        pattern={type == 'number' ? '[0-9]*' : pattern ? pattern : undefined}
        onChange={e => (onChange ? onChange(e.target.value) : undefined)}
        onBlur={() => (onBlur ? onBlur() : undefined)}
        onKeyDown={e => (onKeyDown ? onKeyDown(e) : undefined)}
      />
      {type == 'password' && (
        <button
          type="button"
          className="outline:none absolute right-3 top-1/2"
          onClick={() =>
            setInputType(inputType == 'text' ? 'password' : 'text')
          }
        >
          {inputType == 'text' ? (
            <p className="text-forest-green font-semibold">Hide</p>
          ) : (
            <p className="text-forest-green font-semibold">Show</p>
          )}
        </button>
      )}
      {error && <p className="mb-0 pt-2 text-xs text-red-600">{error}</p>}
    </div>
  )
}

export default Input
